import React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import type { ParticipantCoverageMetricRow } from "../../../services/dataCoverageService";
import { filterCommonFitbitHealthKitMetrics } from "../../../constants/dataCoverageCommonMetrics";

interface ParticipantCoverageSummaryTableProps {
  rows: ParticipantCoverageMetricRow[];
}

const MAX_VISIBLE_ROWS = 20;
const ROW_HEIGHT = 37; // pixels per row
const TABLE_MAX_HEIGHT = `${MAX_VISIBLE_ROWS * ROW_HEIGHT}px`;

const ParticipantCoverageSummaryTable: React.FC<
  ParticipantCoverageSummaryTableProps
> = ({ rows }) => {
  const [showCommonOnly, setShowCommonOnly] = React.useState(false);
  const [openMetricLabel, setOpenMetricLabel] = React.useState<string | null>(
    null,
  );
  const [openMissingDates, setOpenMissingDates] = React.useState<string[]>([]);
  const [copiedSnackbar, setCopiedSnackbar] = React.useState(false);

  const handleOpenMissingDates = (
    metricLabel: string,
    missingDates: string[],
  ) => {
    setOpenMetricLabel(metricLabel);
    setOpenMissingDates(missingDates);
  };

  const handleCopyMissingDates = () => {
    const textToCopy = openMissingDates.join("\n");
    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopiedSnackbar(true);
    });
  };

  const displayedRows = showCommonOnly
    ? filterCommonFitbitHealthKitMetrics(rows)
    : rows;

  return (
    <>
      <Box sx={{ mb: 1 }}>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={showCommonOnly}
              onChange={(event) => setShowCommonOnly(event.target.checked)}
            />
          }
          label="Show only metrics available in both Fitbit and HealthKit"
        />
      </Box>

      <TableContainer
        component={Paper}
        sx={{ maxHeight: TABLE_MAX_HEIGHT, overflowY: "auto" }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Metric</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>First Date</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Last Date</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">
                Days w/ Data
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">
                Coverage %
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">
                Missing Data
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedRows.map((row) => (
              <TableRow key={row.metricKey} hover>
                <TableCell>{row.metricLabel}</TableCell>
                <TableCell>{row.firstDate || "-"}</TableCell>
                <TableCell>{row.lastDate || "-"}</TableCell>
                <TableCell align="right">{row.daysWithData}</TableCell>
                <TableCell align="right">
                  {row.coveragePct.toFixed(1)}%
                </TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      handleOpenMissingDates(row.metricLabel, row.missingDates)
                    }
                    disabled={row.missingDataCount === 0}
                  >
                    {row.missingDataCount}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {displayedRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="textSecondary">
                    No common Fitbit and HealthKit metrics found for this range.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={Boolean(openMetricLabel)}
        onClose={() => setOpenMetricLabel(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>Missing Days: {openMetricLabel}</span>
          <IconButton
            aria-label="close"
            onClick={() => setOpenMetricLabel(null)}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {openMissingDates.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              No missing days found.
            </Typography>
          ) : (
            <>
              <Stack spacing={0.75}>
                {openMissingDates.map((day) => (
                  <Box key={day}>
                    <Typography variant="body2">{day}</Typography>
                  </Box>
                ))}
              </Stack>
              <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleCopyMissingDates}
                >
                  Copy List
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={copiedSnackbar}
        autoHideDuration={2000}
        onClose={() => setCopiedSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setCopiedSnackbar(false)} severity="success">
          Copied to clipboard
        </Alert>
      </Snackbar>
    </>
  );
};

export default ParticipantCoverageSummaryTable;
